// Locomotion (§2.3), jump (§2.4, also while carrying), gravity, tumble and DI (§2.8), slips and
// falling drag (§2.9, §3.3), the respawn parachute (§4.3) and puppet dead-reckoning (§18.2).
// Ghost flight lives in ghosts.ts, balloon flight in balloon.ts and plunger yanks in yank.ts.
// Gravity integrates exactly for constant acceleration, so the jump apex and airtime match the
// closed-form numbers of §2.4.
import { DT, TICK_HZ, TUNE, VOID } from '../constants';
import { EV, emit } from '../events';
import { BS } from '../ids';
import { MAPS } from '../maps/index';
import { createGroundHit } from '../maps/mapTypes';
import { MODES } from '../modes/index';
import { DEG, approachVec, clampLength, rotateToward, v2 } from '../math';
import type { Bean, Vec2, World } from '../types';
import { moveFloat } from './balloon';
import { datan2 } from './dmath';
import { isGrounded } from './edges';
import { moveGhost } from './ghosts';
import { STATE_TABLE } from './stateTable';
import { moveYanked } from './yank';

const LOCKED_TURN = TUNE.bean.turnRateLockedDeg * DEG * DT;
const slide = v2();

/** Target ground speed: run speed × the state's move scale × the mode's speed mult × Big Shake. */
export function moveSpeed(w: World, b: Bean): number {
  const shake = b.shakeTicks > 0 ? TUNE.shake.speedMult : 1;
  return TUNE.bean.runSpeed * STATE_TABLE[b.state].moveScale * MODES[w.cfg.modeId].speedMult(w, b.slot) * shake;
}

/** Standing on the ground and not moving up. */
function onGround(b: Bean): boolean {
  return isGrounded(b) && b.vz <= 0;
}

/**
 * Facing (§2.3) snaps to the move input above `facingMin`; during LUNGE, CARRYING and
 * PUNCH_WINDUP it turns toward it at most `turnRateLockedDeg` per second. States without move
 * control keep their facing.
 */
export function updateFacing(b: Bean, move: Vec2): void {
  if (Math.hypot(move.x, move.y) <= TUNE.bean.facingMin) return;
  const target = datan2(move.y, move.x);
  if (b.state === BS.LUNGE || b.state === BS.CARRYING || b.state === BS.PUNCH_WINDUP) {
    b.facing = rotateToward(b.facing, target, LOCKED_TURN);
    return;
  }
  const rule = STATE_TABLE[b.state].move;
  if (rule === 'full' || rule === 'scaled' || rule === 'descend' || rule === 'float' || rule === 'ghost') b.facing = target;
}

export function launchJump(w: World, b: Bean): void {
  b.vz = w.rules.jumpVz;
  b.jumpBufferTicks = 0;
  b.coyoteTicks = 0;
  emit(w, EV.JUMP, b.slot);
}

/** Jump is a plain jump in NORMAL and a jump with the victim in hand while CARRYING (§2.14). */
export function canJump(b: Bean): boolean {
  const rule = STATE_TABLE[b.state].jump;
  return rule === 'yes' || rule === 'carry';
}

/**
 * Input phase: Jump in a state that allows it launches from the ground (or within coyote time);
 * in the air it is buffered and fires on landing. The buffer is set one higher than
 * `bufferTicks` because the timer phase decrements it before this tick's landing check.
 */
export function handleJump(w: World, b: Bean, pressed: boolean): void {
  if (!pressed || !canJump(b)) return;
  if (onGround(b) || b.coyoteTicks > 0) launchJump(w, b);
  else b.jumpBufferTicks = TUNE.jump.bufferTicks + 1;
}

/** Velocity steering (§2.3): `v += clampLength(target − v, a·dt)`; air control is a fraction of accel. */
function steer(w: World, b: Bean, move: Vec2, maxSpeed: number, ground: boolean): void {
  if (Math.hypot(move.x, move.y) >= TUNE.bean.noInputThreshold) {
    const accel = ground ? w.rules.accel : w.rules.accel * TUNE.bean.airControl;
    approachVec(b.vel, move.x * maxSpeed, move.y * maxSpeed, accel * DT);
  } else {
    approachVec(b.vel, 0, 0, (ground ? w.rules.decel : TUNE.bean.airDrag) * DT);
  }
}

function applyGravity(w: World, b: Bean): void {
  if (b.state !== BS.FALLING && b.vz === 0 && b.z <= b.groundZ) return;
  const g = w.rules.gravity;
  b.z += b.vz * DT - 0.5 * g * DT * DT;
  b.vz -= g * DT;
}

/** Map slide (teacup tilt) on grounded bodies, the speed cap, gravity, and the position update. */
function integrate(w: World, b: Bean): void {
  const map = MAPS[w.cfg.mapId];
  if (map.slideAccel && b.state !== BS.FALLING && onGround(b)) {
    map.slideAccel(w, b.pos.x, b.pos.y, slide);
    b.vel.x += slide.x * DT;
    b.vel.y += slide.y * DT;
  }
  clampLength(b.vel, b.vel.x, b.vel.y, TUNE.collide.maxSpeed);
  applyGravity(w, b);
  b.pos.x += b.vel.x * DT;
  b.pos.y += b.vel.y * DT;
}

/**
 * TUMBLE (§2.8): ground deceleration and no control for the first ticks, then exit below the exit
 * speed or directional influence that turns the velocity without changing its magnitude.
 */
function tumble(b: Bean, move: Vec2): void {
  const t = TUNE.tumble;
  const v = b.vel;
  const speed = Math.hypot(v.x, v.y);
  if (speed > 0 && isGrounded(b)) {
    const k = Math.max(0, speed - t.decel * DT) / speed;
    v.x *= k;
    v.y *= k;
  }
  if (b.st < t.noControlTicks) return;
  const s0 = Math.hypot(v.x, v.y);
  if (s0 < t.exitSpeed) {
    b.state = BS.NORMAL;
    b.st = 0;
    return;
  }
  if (Math.hypot(move.x, move.y) < TUNE.bean.noInputThreshold) return;
  const ux = v.x / s0;
  const uy = v.y / s0;
  const along = move.x * ux + move.y * uy;
  v.x += (move.x - along * ux) * t.diAccel * DT;
  v.y += (move.y - along * uy) * t.diAccel * DT;
  const k = s0 / Math.hypot(v.x, v.y);
  v.x *= k;
  v.y *= k;
}

/**
 * How far inside the floor's rim a parachute over a hole aims when it drifts back (INT-A): the
 * landing then has floor all around the body.
 */
export const PARA_FLOOR_INSET = 0.6;
const paraHit = createGroundHit();
const paraMove = v2();

/**
 * RESPAWN: offstage while the respawn delay runs, then a linear parachute descent with 50% steering.
 * Over a hole (a Crumble Cake tile that fell during the descent, or steered off the floor) the
 * umbrella drifts back over the nearest floor instead (INT-A cheap-death pass: 6.5% of Crumble
 * Cake parachutes used to come down in the milk, straight back to another respawn).
 */
function descend(w: World, b: Bean, move: Vec2): void {
  if (b.respawnTicks > 0) return;
  const perTick = TUNE.respawn.paraZ / TUNE.respawn.paraTicks;
  b.vz = -perTick * TICK_HZ;
  b.z -= perTick;
  const map = MAPS[w.cfg.mapId];
  let steerBy = move;
  if (map.groundAt(w, b.pos.x, b.pos.y) === VOID) {
    map.nearestGround(w, b.pos.x, b.pos.y, paraHit);
    const dx = paraHit.x + paraHit.nx * PARA_FLOOR_INSET - b.pos.x;
    const dy = paraHit.y + paraHit.ny * PARA_FLOOR_INSET - b.pos.y;
    const l = Math.hypot(dx, dy);
    if (l > 1e-6) {
      paraMove.x = dx / l;
      paraMove.y = dy / l;
      steerBy = paraMove;
    }
  }
  steer(w, b, steerBy, moveSpeed(w, b), true);
  b.pos.x += b.vel.x * DT;
  b.pos.y += b.vel.y * DT;
}

/** Uncontrolled states: true when the body still integrates (ballistic), false when it is placed by its own logic. */
function passive(w: World, b: Bean): boolean {
  switch (b.state) {
    case BS.FALLING:
      b.vel.x *= TUNE.edge.fallDragPerTick;
      b.vel.y *= TUNE.edge.fallDragPerTick;
      return true;
    case BS.PUNCH:
      if (onGround(b)) approachVec(b.vel, 0, 0, w.rules.decel * DT);
      return true;
    case BS.SLIP:
      return true;
    case BS.YANKED:
      moveYanked(b);
      return false;
    default:
      return false;
  }
}

/** Step phase 4 for one non-puppet bean: integrate by the state's move rule (§2.14). */
export function moveBean(w: World, b: Bean, move: Vec2): void {
  switch (STATE_TABLE[b.state].move) {
    case 'full':
    case 'scaled':
      steer(w, b, move, moveSpeed(w, b), onGround(b));
      break;
    case 'locked':
      b.vel.x = b.lungeDir.x * TUNE.shove.lungeSpeed;
      b.vel.y = b.lungeDir.y * TUNE.shove.lungeSpeed;
      break;
    case 'di':
      tumble(b, move);
      break;
    case 'float':
      moveFloat(w, b, move);
      return;
    case 'descend':
      descend(w, b, move);
      return;
    case 'ghost':
      moveGhost(w, b, move);
      return;
    case 'recover':
    case 'ledge':
      return;
    case 'none':
      if (!passive(w, b)) return;
      break;
  }
  integrate(w, b);
}

/** Predict mode: a remote puppet coasts on its velocity for a few ticks past the snapshot, then holds. */
export function deadReckon(w: World, b: Bean): void {
  if (w.predictBaseTick === undefined || w.tick - w.predictBaseTick > TUNE.predict.puppetTicks) return;
  b.pos.x += b.vel.x * DT;
  b.pos.y += b.vel.y * DT;
}
